import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { logout } from '../utilities';
import { useAuth } from '../context/AuthContext';
import './TopMenu.css';

function TopMenu() {
  const { isAuthenticated, setIsAuthenticated, userInfo } = useAuth();

  const [customerName, setCustomerName] = useState('');
  const [locationNames, setLocationNames] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);

  const menuRef = useRef(null);

  const token = sessionStorage.getItem('jwtToken');
  const customerId = userInfo?.customer_id;
  const email = userInfo?.email || '';
  const role = userInfo?.role || '';

  const roleLabel = role
    ? role
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (character) => character.toUpperCase())
    : 'Gebruiker';

  const userInitial = email?.charAt(0).toUpperCase() || '?';
  const locationsLabel = locationNames.join(', ');

  const fetchCustomer = useCallback(
    async (id) => {
      if (!token || !id) return;

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/customer/getcustomername/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        setCustomerName(response.data?.data?.name || '');
      } catch (error) {
        console.error('Error fetching customer data:', error);
      }
    },
    [token]
  );

  const fetchLocations = useCallback(async () => {
    if (!token || !isAuthenticated || !userInfo) {
      setLocationNames([]);
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/location/allLocations`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const allLocations = response.data?.data || [];
      const userLocationIds = userInfo?.user_location_ids || [];

      const userLocNames = allLocations
        .filter((location) =>
          userLocationIds.includes(location.location_id)
        )
        .map((location) => location.location_name);

      setLocationNames(userLocNames);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  }, [token, isAuthenticated, userInfo]);

  useEffect(() => {
    if (isAuthenticated && customerId) {
      fetchCustomer(customerId);
    } else {
      setCustomerName('');
    }
  }, [isAuthenticated, customerId, fetchCustomer]);

  useEffect(() => {
    if (isAuthenticated && userInfo) {
      fetchLocations();
    } else {
      setLocationNames([]);
    }
  }, [isAuthenticated, userInfo, fetchLocations]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);

  const handleLogout = () => {
    try {
      logout();
      setIsAuthenticated(false);
      window.location.href = '/login';
    } catch (error) {
      console.error('An error occurred during logout:', error);
    }
  };

  return (
    <header className="tm-header">
      <div className="tm-inner">
        <div className="tm-brand">
          <div className="tm-brand-icon" aria-hidden="true">
            <i className="bi bi-mortarboard-fill" />
          </div>

          <div className="tm-brand-copy">
            <span className="tm-brand-title">Mijn Ouder App</span>
            <span className="tm-brand-subtitle">Schoolportaal</span>
          </div>
        </div>

        {isAuthenticated ? (
          <div className="tm-user-bar">
            <div className="tm-context">
              {customerName && (
                <div className="tm-pill" title={customerName}>
                  <i className="bi bi-building" />
                  <span>{customerName}</span>
                </div>
              )}

              {locationNames.length > 0 && (
                <div className="tm-pill tm-location-pill" title={locationsLabel}>
                  <i className="bi bi-geo-alt" />
                  <span>{locationsLabel}</span>
                </div>
              )}

              {roleLabel && (
                <div className="tm-pill">
                  <i className="bi bi-shield-check" />
                  <span>{roleLabel}</span>
                </div>
              )}
            </div>

            <div className="tm-account" ref={menuRef}>
              <button
                type="button"
                className={`tm-account-button ${
                  menuOpen ? 'tm-account-button-active' : ''
                }`}
                onClick={() => setMenuOpen((previous) => !previous)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="Accountmenu openen"
              >
                <span className="tm-avatar">{userInitial}</span>

                <span className="tm-account-copy">
                  <span className="tm-account-label">Account</span>
                  <span className="tm-account-email">{email}</span>
                </span>

                <i
                  className={`bi bi-chevron-down tm-chevron ${
                    menuOpen ? 'tm-chevron-open' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>

              <div
                className={`tm-dropdown ${
                  menuOpen ? 'tm-dropdown-open' : ''
                }`}
                role="menu"
                aria-hidden={!menuOpen}
              >
                <div className="tm-dropdown-profile">
                  <div className="tm-dropdown-avatar">{userInitial}</div>

                  <div className="tm-dropdown-user">
                    <strong>{roleLabel}</strong>
                    <span>{email}</span>
                  </div>
                </div>

                <div className="tm-dropdown-context">
                  {customerName && (
                    <div className="tm-dropdown-detail">
                      <i className="bi bi-building" />
                      <div>
                        <small>Organisatie</small>
                        <span>{customerName}</span>
                      </div>
                    </div>
                  )}

                  {locationNames.length > 0 && (
                    <div className="tm-dropdown-detail">
                      <i className="bi bi-geo-alt" />
                      <div>
                        <small>Locatie</small>
                        <span>{locationsLabel}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="tm-dropdown-divider" />

                <button
                  type="button"
                  className="tm-dropdown-item"
                  onClick={handleLogout}
                  role="menuitem"
                >
                  <i className="bi bi-box-arrow-right" />
                  <span>Uitloggen</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            id="loginButtonOnTop"
            type="button"
            className="tm-login-button"
            onClick={() => {
              window.location.href = '/login';
            }}
          >
            <i className="bi bi-box-arrow-in-right" />
            <span>Inloggen</span>
          </button>
        )}
      </div>
    </header>
  );
}

export default TopMenu;